import { Link } from "react-router-dom";
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import axios from '../../services/AxiosInstance';
import { Button, Spinner, Modal } from "react-bootstrap";
import { selectCompany } from '../../store/actions/AuthActions';
import { getApiUrl } from "../../utils/ApiUtils";
import { logout } from '../../store/actions/AuthActions';
import { useSelector } from 'react-redux';
import { getUid } from '../../store/selectors/AuthSelectors';
import { Building2, Plus, Trash2, LogOut, Users, Calendar, AlertTriangle } from 'lucide-react';
import useWindowSize, { isMobile } from '../../hooks/useWindowSize';

import loginbg from "../../images/bg-login.jpg";

// Enhanced CSS styles
const styles = {
  selectCompanyPage: {
    minHeight: '100vh',
    display: 'flex',
    backgroundImage: `url(${loginbg})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat'
  },
  pageWrapper: {
    display: 'flex',
    width: '100%',
    minHeight: '100vh'
  },
  leftPanel: {
    flex: '1',
    background: 'rgba(0, 0, 0, 0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3rem',
    color: 'white'
  },
  rightPanel: {
    flex: '1',
    background: 'rgba(255, 255, 255, 0.98)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
    backdropFilter: 'blur(10px)',
    overflowY: 'auto'
  },
  contentCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '3rem',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    width: '100%',
    maxWidth: '600px',
    border: '1px solid #e5e7eb',
    margin: '2rem 0'
  },
  headerSection: {
    marginBottom: '2rem',
    textAlign: 'center'
  },
  companyCard: {
    background: '#f8fafc',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    padding: '1.5rem',
    marginBottom: '1rem',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    position: 'relative'
  },
  companyCardHover: {
    borderColor: '#3b82f6',
    backgroundColor: '#eff6ff',
    transform: 'translateY(-2px)',
    boxShadow: '0 10px 25px rgba(59, 130, 246, 0.1)'
  },
  companyInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem'
  },
  companyIcon: {
    background: 'var(--bs-primary)',
    color: 'white',
    borderRadius: '8px',
    padding: '0.75rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  companyDetails: {
    flex: 1
  },
  companyName: {
    fontSize: '1.125rem',
    fontWeight: '600',
    color: '#1f2937',
    marginBottom: '0.25rem'
  },
  companyMeta: {
    fontSize: '0.875rem',
    color: '#6b7280'
  },
  deleteButton: {
    position: 'absolute',
    top: '1rem',
    right: '1rem',
    background: 'none',
    border: 'none',
    color: '#9ca3af',
    cursor: 'pointer',
    padding: '0.5rem',
    borderRadius: '4px',
    transition: 'all 0.2s ease'
  },
  deleteButtonHover: {
    color: '#dc2626',
    backgroundColor: '#fef2f2'
  },
  emptyState: {
    textAlign: 'center',
    padding: '3rem 1rem',
    color: '#6b7280'
  },
  createButton: {
    width: '100%',
    padding: '1rem',
    background: 'var(--bs-primary)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem'
  },
  separator: {
    display: 'flex',
    alignItems: 'center',
    textAlign: 'center',
    color: '#9ca3af',
    margin: '2rem 0'
  },
  separatorLine: {
    flex: 1,
    height: '1px',
    background: '#e5e7eb'
  },
  separatorText: {
    padding: '0 1rem',
    fontSize: '0.875rem',
    backgroundColor: 'white'
  },
  logoutButton: {
    background: 'none',
    border: 'none',
    color: '#6b7280',
    fontSize: '0.875rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    transition: 'color 0.2s ease'
  },
  loadingSpinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #e5e7eb',
    borderTop: '4px solid #3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  }
};

// Mobile-specific style overrides (applied when screen ≤ 768px)
const mobileStyles = {
  pageWrapper: {
    flexDirection: 'column'
  },
  leftPanel: {
    minHeight: '200px',
    padding: '2rem 1.5rem',
    flex: 'none'
  },
  rightPanel: {
    padding: '1.5rem 1rem',
    flex: 'none'
  },
  contentCard: {
    padding: '2rem 1.5rem',
    boxShadow: 'none',
    borderRadius: '0'
  }
};

// Helper function to merge mobile styles
const getResponsiveStyle = (baseStyle, mobileStyle, isMobile) => {
  return isMobile && mobileStyle ? { ...baseStyle, ...mobileStyle } : baseStyle;
};

function SelectCompany(props) {

  const uid = useSelector((state) => getUid(state));

  // Mobile responsiveness
  const { width } = useWindowSize();
  const mobile = isMobile(width);

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ show: false, companyId: null, companyName: '' });
  const [hoveredCard, setHoveredCard] = useState(null);
  const [hoveredDelete, setHoveredDelete] = useState(null);
  const dispatch = useDispatch();
  const history = useHistory();

  const getCompaniesUrl = getApiUrl(`auth/users/companies?googleId=${uid}`);

  useEffect(() => {
    setLoading(true);
    axios.get(getCompaniesUrl, { withCredentials: true })
      .then(response => {
        setCompanies(response.data.companies);
      })
      .catch(error => {
        console.error('Error fetching companies:', error);
      })
      .finally(() => setLoading(false));
  }, [dispatch, history]);

  const handleCompanyClick = (companyId, companyName, companyRegNumber) => {

    console.log('Selected company:', companyId, companyName, companyRegNumber);

    dispatch(selectCompany(companyId, companyName, companyRegNumber, history));
  };

  const handleDelete = () => {
    const deleteCompanyUrl = getApiUrl(`auth/company/${deleteModal.companyId}`);
    axios.delete(deleteCompanyUrl, { withCredentials: true })
      .then(response => {
        setCompanies(companies.filter(company => company.company_id !== deleteModal.companyId));
      })
      .catch(error => {
        console.error('Error deleting company:', error);
      })
      .finally(() => setDeleteModal({ show: false, companyId: null, companyName: '' }));
  };

  const handleLogout = () => {
    dispatch(logout(history));
  };

  const handleCreateCompany = () => {
    history.push('/create-company');
  };

  return (
    <>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .create-button:hover {
          transform: translateY(-1px);
          box-shadow: 0 10px 25px rgba(5, 179, 17, 0.3);
        }
        
        .logout-button:hover {
          color: #374151 !important;
        }
      `}</style>

      <div style={styles.selectCompanyPage}>
        <div style={getResponsiveStyle(styles.pageWrapper, mobileStyles.pageWrapper, mobile)}>
          {/* Left Panel */}
          <div className="left-panel" style={getResponsiveStyle(styles.leftPanel, mobileStyles.leftPanel, mobile)}>
            <div style={{ textAlign: 'center', maxWidth: '400px' }}>
              <h1 style={{
                fontSize: '3rem',
                fontWeight: '700',
                marginBottom: '1rem',
                textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                color: 'white'
              }}>
                LedgerLoop AI
              </h1>
              <p style={{
                fontSize: '1.25rem',
                marginBottom: '3rem',
                opacity: 0.95,
                textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                lineHeight: 1.4
              }}>
                Hantera flera företag smidigt – växla enkelt mellan ekonomier
              </p>

              {/* Simple footer */}
              <div style={{
                marginTop: 'auto',
                fontSize: '0.875rem',
                opacity: 0.8,
                textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
              }}>
                <p style={{ marginBottom: '1rem' }}>© 2025 Riza Consulting AB</p>
                <div style={{
                  display: 'flex',
                  gap: '1rem',
                  justifyContent: 'center',
                  flexWrap: 'wrap'
                }}>
                  <a
                    href="https://www.ledgerloop.ai/privacy"
                    style={{
                      color: 'rgba(255,255,255,0.9)',
                      textDecoration: 'none',
                      fontSize: '0.875rem'
                    }}
                  >
                    Integritetspolicy
                  </a>
                  <span style={{ opacity: 0.7 }}>•</span>
                  <a
                    href="https://www.ledgerloop.ai/terms"
                    style={{
                      color: 'rgba(255,255,255,0.9)',
                      textDecoration: 'none',
                      fontSize: '0.875rem'
                    }}
                  >
                    Användarvillkor
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel */}
          <div className="right-panel" style={getResponsiveStyle(styles.rightPanel, mobileStyles.rightPanel, mobile)}>
            <div style={getResponsiveStyle(styles.contentCard, mobileStyles.contentCard, mobile)}>
              {/* Header */}
              <div style={styles.headerSection}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <Link
                    to="/login"
                    style={{
                      fontSize: '1.5rem',
                      fontWeight: '700',
                      color: 'var(--bs-primary',
                      textDecoration: 'none'
                    }}
                  >
                    LedgerLoop
                  </Link>
                  <button
                    className="logout-button"
                    data-testid="logout-button"
                    style={styles.logoutButton}
                    onClick={handleLogout}
                  >
                    <LogOut size={16} />
                    Logga ut
                  </button>
                </div>

                <h2 style={{ fontSize: '1.875rem', fontWeight: '700', color: '#1f2937', marginBottom: '0.5rem' }}>
                  Välj företag
                </h2>
                <p style={{ color: '#6b7280', fontSize: '1rem' }}>
                  {companies.length > 0
                    ? 'Välj det företag du vill administrera'
                    : 'Du har ännu inga företag registrerade'
                  }
                </p>
              </div>

              {/* Loading State */}
              {loading && (
                <div data-testid="loading-state" style={{ textAlign: 'center', padding: '3rem' }}>
                  <div data-testid="loading-spinner" style={styles.loadingSpinner}></div>
                  <p style={{ marginTop: '1rem', color: '#6b7280' }}>Laddar företag...</p>
                </div>
              )}

              {/* Companies List */}
              {!loading && companies.length > 0 && (
                <div data-testid="companies-list" style={{ marginBottom: '2rem' }}>
                  {companies.map(company => (
                    <div
                      key={company.company_id}
                      data-testid={`company-card-${company.company_id}`}
                      style={{
                        ...styles.companyCard,
                        ...(hoveredCard === company.company_id ? styles.companyCardHover : {})
                      }}
                      onMouseEnter={() => setHoveredCard(company.company_id)}
                      onMouseLeave={() => setHoveredCard(null)}
                      onClick={() => handleCompanyClick(company.company_id, company.company_name, company.registration_number)}
                    >
                      <button
                        data-testid={`delete-button-${company.company_id}`}
                        style={{
                          ...styles.deleteButton,
                          ...(hoveredDelete === company.company_id ? styles.deleteButtonHover : {})
                        }}
                        onMouseEnter={() => setHoveredDelete(company.company_id)}
                        onMouseLeave={() => setHoveredDelete(null)}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteModal({
                            show: true,
                            companyId: company.company_id,
                            companyName: company.company_name
                          });
                        }}
                      >
                        <Trash2 size={16} />
                      </button>

                      <div style={styles.companyInfo}>
                        <div style={styles.companyIcon}>
                          <Building2 size={24} />
                        </div>
                        <div style={styles.companyDetails}>
                          <h3 data-testid={`company-name-${company.company_id}`} style={styles.companyName}>{company.company_name}</h3>
                          <p data-testid={`company-org-number-${company.company_id}`} style={styles.companyMeta}>
                            Org. Nr: {company.registration_number}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Empty State */}
              {!loading && companies.length === 0 && (
                <div data-testid="empty-state" style={styles.emptyState}>
                  <Building2 size={64} style={{ color: '#d1d5db', marginBottom: '1rem' }} />
                  <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem', color: '#374151' }}>
                    Inga företag registrerade
                  </h3>
                  <p style={{ marginBottom: '2rem' }}>
                    Registrera ditt första företag för att komma igång med LedgerLoop AI
                  </p>
                </div>
              )}

              {/* Separator */}
              {!loading && companies.length > 0 && (
                <div style={styles.separator}>
                  <div style={styles.separatorLine}></div>
                  <span style={styles.separatorText}>eller</span>
                  <div style={styles.separatorLine}></div>
                </div>
              )}

              {/* Create Company Button */}
              {!loading && (
                <button
                  className="create-button"
                  data-testid="create-company-button"
                  style={styles.createButton}
                  onClick={handleCreateCompany}
                >
                  <Plus size={20} />
                  Registrera ett nytt företag
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        <Modal
          show={deleteModal.show}
          onHide={() => setDeleteModal({ show: false, companyId: null, companyName: '' })}
          size="lg"
        >
          <Modal.Header
            closeButton
            style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}
          >
            <Modal.Title style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <AlertTriangle size={24} style={{ color: '#dc2626' }} />
              Bekräfta borttagning
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: '2rem' }}>
            <p style={{ fontSize: '1rem', marginBottom: '1rem' }}>
              Är du säker på att du vill ta bort företaget{' '}
              <strong data-testid="delete-company-name" style={{ color: '#dc2626' }}>{deleteModal.companyName}</strong>?
            </p>
            <div style={{
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              padding: '1rem',
              fontSize: '0.875rem',
              color: '#dc2626'
            }}>
              <strong>Varning:</strong> Denna åtgärd kan inte ångras. All data kopplad till detta företag kommer att tas bort permanent.
            </div>
          </Modal.Body>
          <Modal.Footer style={{ borderTop: '1px solid #e5e7eb', padding: '1rem 2rem' }}>
            <Button
              variant="secondary"
              data-testid="cancel-delete-button"
              onClick={() => setDeleteModal({ show: false, companyId: null, companyName: '' })}
              style={{ padding: '0.5rem 1rem' }}
            >
              Avbryt
            </Button>
            <Button
              variant="danger"
              data-testid="confirm-delete-button"
              onClick={handleDelete}
              style={{ padding: '0.5rem 1rem' }}
            >
              <Trash2 size={16} style={{ marginRight: '0.5rem' }} />
              Ta bort företag
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
}

export default SelectCompany;
